import React, { useMemo } from 'react';
import { Partner } from '@marageti/z4-sdk/lib/travel';
import { useParams } from 'react-router-dom';
import { useApiClient, useFetch } from '@marageti/z4-lib';
import PartnerTripsView from './trips-view';
import useTripMatchSearch from '../../../hooks/use-trip-match-search';
import useURLSearchQuery from '../../../hooks/use-url-for-search-query';
import { sortByValues } from './options';

interface DefaultSearch {
  [key: string]: string | string[];
  assignedTo: string;
  countries: string[];
  departureDateFrom: string;
  departureDateTo: string;
  fullText: string;
  matchDateFrom: string;
  matchDateTo: string;
  order: string;
  sort: string;
  status: string[];
}

const defaultSearch: DefaultSearch = {
  assignedTo: '',
  countries: [],
  departureDateFrom: '',
  departureDateTo: '',
  fullText: '',
  matchDateFrom: '',
  matchDateTo: '',
  order: sortByValues.createdAt.order,
  sort: sortByValues.createdAt.field,
  status: [],
};

const PartnerTripsContainer = () => {
  const { partnerId } = useParams();
  const apiClient = useApiClient();
  const { data } = useFetch<Partner>(apiClient.partnersClient.getPartner, [partnerId], !!partnerId);

  const partnerCountryOptions = useMemo(() => {
    if (data) {
      return data.primaryCountries.map((c) => ({ country: c.country }));
    }
    return [];
  }, [data]);

  const [queryValues, updateURLQuery] = useURLSearchQuery<DefaultSearch>(defaultSearch);
  const {
    assignedTo,
    countries,
    departureDateFrom,
    departureDateTo,
    fullText,
    matchDateFrom,
    matchDateTo,
    sort,
    status,
  } = queryValues;

  // Convert assignedTo filter value to search array
  const assignedToFilter = (() => {
    if (!assignedTo || assignedTo === 'all') return [];
    if (assignedTo === 'none') return [''];
    return [assignedTo];
  })();

  const sortConfig = sortByValues[sort as keyof typeof sortByValues]
    ?? sortByValues.createdAt;

  const {
    tripMatches,
    error,
    hasMore,
    isLoading,
    loadMore,
    totalHits,
  } = useTripMatchSearch({
    ...queryValues,
    assignedTo: assignedToFilter,
    partner: partnerId || '',
    sort: sortConfig.field,
    order: sortConfig.order,
  });

  const onChangeFilter = (name: string, v: string | string[]) => {
    switch (name) {
      case 'sort':
        updateURLQuery({
          sort: v,
          order: sortByValues[v as keyof typeof sortByValues].order,
        });
        break;
      case 'countries':
        updateURLQuery({ countries: v });
        break;
      case 'fullText':
        updateURLQuery({ fullText: v });
        break;
      case 'status':
        updateURLQuery({ status: v });
        break;
      case 'assignedTo': {
        const value = v as string;
        updateURLQuery({ assignedTo: value === 'all' ? '' : value });
        break;
      }
      case 'matchDateFrom':
      case 'matchDateTo':
      case 'departureDateFrom':
      case 'departureDateTo':
        updateURLQuery({ [name]: typeof v === 'string' ? v : '' });
        break;
      default:
        break;
    }
  };

  const formattedTMCount = new Intl.NumberFormat('en-US').format(totalHits);

  return (
    <PartnerTripsView
      assignedTo={assignedTo}
      countries={countries}
      departureDateFrom={departureDateFrom}
      departureDateTo={departureDateTo}
      disableSortBy={fullText.length > 0}
      error={error}
      hasMoreItems={hasMore}
      isLoading={isLoading}
      items={tripMatches}
      loadMore={loadMore}
      matchDateFrom={matchDateFrom}
      matchDateTo={matchDateTo}
      onChangeFilter={onChangeFilter}
      partnerCountryOptions={partnerCountryOptions}
      partnerAgents={data?.agents || []}
      searchValue={fullText}
      selectedStatusChips={status}
      sortBy={sort}
      totalHits={formattedTMCount}
    />
  );
};

export default PartnerTripsContainer;
